/**
 * The input side of a game screen, solo or duel: the `InputSession`, the
 * touch-controls element it binds to, and the DAS/ARR timings it follows.
 *
 * ## Why both halves are `$state`
 *
 * `TouchControls`' element exists before the screen's `onMount` has created
 * the session (a child's effects flush before its parent's `onMount`), so
 * whichever of the two arrives second has to be what triggers the binding. A
 * plain field for either would make that impossible, and the failure is
 * silent: no listener is attached and only a keyboard can play.
 * `docs/decisions/0024`.
 *
 * Construct it during component initialisation (it registers effects), bind
 * `touchRoot` to `TouchControls`, and call `start` from `onMount`.
 */
import { InputSession } from '../input/input-session.js';
import { settings } from '../state/settings.svelte.js';

export class PlayInput {
  /** The touch-controls root, bound by the screen: `<TouchControls bind:root={input.touchRoot} />`. */
  touchRoot = $state<HTMLElement | undefined>();
  private session = $state<InputSession | undefined>();

  constructor() {
    $effect(() => {
      const root = this.touchRoot;
      const session = this.session;
      if (root === undefined || session === undefined) return;
      session.attachTouch(root);
    });

    // DAS and ARR follow the Settings steppers live, in the run in progress.
    $effect(() => {
      this.session?.setConfig({ dasMs: settings.dasMs, arrMs: settings.arrMs });
    });
  }

  /** Creates the session. Call once, from `onMount`; `stop` is its cleanup. */
  start(): InputSession {
    const session = new InputSession({
      config: { dasMs: settings.dasMs, arrMs: settings.arrMs },
    });
    this.session = session;
    return session;
  }

  stop(): void {
    this.session?.destroy();
    this.session = undefined;
  }
}
